import React, { useEffect, useState } from "react";
import { buildPublicFileUrl } from "../../../../services/config";
import { getApi } from "../../../../services/api/globalApi";
import { PiCopySimpleBold, PiImageSquareFill, PiXFill } from "react-icons/pi";
import { successMessage } from "../../../../utils/toastify-message/ToastifyMessage";

const { forwardRef, useImperativeHandle } = React;

const ImagesModal = forwardRef(({ selectedImages, selectImg = true }, ref) => {
  const [showModal, setShowModal] = useState(false);
  const [fileData, setFileData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [searchFile, setSearchFile] = useState("");
  const [loading, setLoading] = useState(false);
  const [pageInput, setPageInput] = useState("1");
  const [pageInputError, setPageInputError] = useState("");
  const itemsPerPage = 24;

  useImperativeHandle(ref, () => ({
    showModal() {
      setCurrentPage(1);
      setPageInput("1");
      setPageInputError("");
      setShowModal((visible) => !visible);
    },
    afterSubmit() {},
  }));

  useEffect(() => {
    if (!showModal) return undefined;
    let active = true;

    const timeout = setTimeout(async () => {
      try {
        setLoading(true);
        const response = await getApi({
          path: "admin/filemanagers/files",
          params: {
            page: currentPage,
            limit: itemsPerPage,
            search: searchFile,
            variant: "original",
            kind: "image",
          },
        });

        if (!active) return;
        setFileData(response?.data?.files || []);
        setTotalPages(response?.data?.pagination?.totalPages || 1);
        setTotalItems(response?.data?.pagination?.total || 0);
      } catch (error) {
        if (!active) return;
        console.log(error);
        setFileData([]);
        setTotalPages(1);
        setTotalItems(0);
      } finally {
        if (active) setLoading(false);
      }
    }, 350);

    return () => { active = false; clearTimeout(timeout); };
  }, [showModal, currentPage, searchFile]);

  useEffect(() => {
    setPageInput(String(currentPage));
    setPageInputError("");
  }, [currentPage]);

  const goToPage = (event) => {
    event.preventDefault();
    const requestedPage = Number(pageInput);

    if (
      !Number.isInteger(requestedPage) ||
      requestedPage < 1 ||
      requestedPage > totalPages
    ) {
      setPageInputError(`شماره صفحه باید بین ۱ تا ${totalPages} باشد.`);
      return;
    }

    setPageInputError("");
    setCurrentPage(requestedPage);
  };

  const selectImage = (item) => {
    selectedImages({ name: item.name, isMain: false });
    setShowModal(false);
  };

  const copyImgUrl = (item) => {
    navigator.clipboard.writeText(
      buildPublicFileUrl(item, { variant: "medium_" })
    );
    successMessage("لینک عکس کپی شد.");
    setShowModal(false);
  };

  const showOriginalWhenPreviewIsMissing = (event, item) => {
    const originalUrl = buildPublicFileUrl(item);
    if (!event.currentTarget.dataset.fallback) {
      event.currentTarget.dataset.fallback = "true";
      event.currentTarget.src = originalUrl;
    }
  };

  return (
    <>
      <div
        className={`fixed top-0 right-0 w-full h-full bg-black/40 transition-all z-30 ${
          !showModal ? "hidden" : ""
        }`}
      />

      <div
        className={`w-[90%] h-[90%] overflow-hidden flex flex-col bg-white shadow-md z-40 rounded-3xl p-4 transition-all fixed top-0 right-[50%] mr-[-45%] opacity-0 invisible ${
          showModal ? "!mt-[5%] !opacity-100 !visible" : ""
        }`}
      >
        <div className="w-full px-6 flex items-center justify-between py-4 border-b-2 shrink-0">
          <h4>آخرین عکس‌ها</h4>
          <PiXFill
            onClick={() => setShowModal(false)}
            size={30}
            className="text-red-400 transition-all hover:text-red-600 cursor-pointer"
          />
        </div>

        <div className="flex-1 overflow-y-auto px-1">
          <div className="max-w-md mx-auto my-3">
            <label
              htmlFor="file-image-search"
              className="mb-2 text-sm font-medium text-gray-900 sr-only"
            >
              جست‌وجو
            </label>
            <input
              id="file-image-search"
              value={searchFile}
              onChange={(event) => {
                setSearchFile(event.target.value);
                setCurrentPage(1);
              }}
              type="search"
              className="bg-white text-center sm:text-right border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
              placeholder="جست‌وجوی عکس..."
            />
          </div>

          {loading ? (
            <div className="py-12 text-center text-gray-500">
              در حال دریافت تصاویر...
            </div>
          ) : fileData.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-4 mt-6">
              {fileData.map((item) => (
                <div
                  key={item._id || item.name}
                  className="min-h-32 overflow-hidden cursor-pointer rounded-lg flex items-center justify-center relative bg-gray-50"
                >
                  <PiCopySimpleBold
                    onClick={() => copyImgUrl(item)}
                    className="absolute top-1 right-1 text-white bg-blue-400 transition-all hover:bg-blue-600 rounded-lg p-1 z-10"
                    size={36}
                  />
                  {selectImg ? (
                    <button
                      type="button"
                      onClick={() => selectImage(item)}
                      className="absolute inset-0 opacity-0 transition-all hover:opacity-100 bg-black/30 text-white flex items-center justify-center z-[5]"
                    >
                      انتخاب
                    </button>
                  ) : null}
                  <img
                    className="h-auto max-w-full rounded-lg"
                    src={buildPublicFileUrl(item, { variant: "medium_" })}
                    onError={(event) =>
                      showOriginalWhenPreviewIsMissing(event, item)
                    }
                    loading="lazy"
                    alt={item.originalName || item.name}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center gap-2 py-12 w-full text-gray-700">
              عکسی یافت نشد
              <PiImageSquareFill size={50} />
            </div>
          )}
        </div>

        <div className="shrink-0 flex flex-wrap items-center justify-center gap-4 pt-4 mt-2 border-t bg-white">
          <button
            type="button"
            disabled={currentPage === 1 || loading}
            onClick={() => setCurrentPage((page) => Math.max(page - 1, 1))}
            className="px-5 py-2 rounded-lg bg-blue-600 text-white disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
          >
            قبلی
          </button>

          <span className="text-sm text-gray-700">
            صفحه {currentPage} از {totalPages} — {totalItems} تصویر
          </span>

          <form
            onSubmit={goToPage}
            className="relative flex items-center gap-2"
          >
            <label
              htmlFor="image-modal-page"
              className="text-sm text-gray-700 whitespace-nowrap"
            >
              رفتن به صفحه
            </label>
            <input
              id="image-modal-page"
              type="number"
              inputMode="numeric"
              min="1"
              max={totalPages}
              value={pageInput}
              disabled={loading}
              onFocus={(event) => event.currentTarget.select()}
              onChange={(event) => {
                setPageInput(event.target.value);
                setPageInputError("");
              }}
              aria-invalid={Boolean(pageInputError)}
              aria-describedby={
                pageInputError ? "image-modal-page-error" : undefined
              }
              className={`h-10 w-20 rounded-lg border bg-white px-2 text-center text-sm outline-none transition focus:ring-2 disabled:cursor-not-allowed disabled:bg-gray-100 ${
                pageInputError
                  ? "border-red-400 focus:border-red-500 focus:ring-red-100"
                  : "border-gray-300 focus:border-blue-500 focus:ring-blue-100"
              }`}
            />
            <button
              type="submit"
              disabled={loading || !pageInput}
              className="h-10 rounded-lg bg-gray-700 px-4 text-sm text-white transition hover:bg-gray-800 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500"
            >
              برو
            </button>
            {pageInputError ? (
              <span
                id="image-modal-page-error"
                className="absolute right-0 bottom-full mb-1 text-xs text-red-500 whitespace-nowrap"
              >
                {pageInputError}
              </span>
            ) : null}
          </form>

          <button
            type="button"
            disabled={currentPage >= totalPages || loading}
            onClick={() =>
              setCurrentPage((page) => Math.min(page + 1, totalPages))
            }
            className="px-5 py-2 rounded-lg bg-blue-600 text-white disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
          >
            بعدی
          </button>
        </div>
      </div>
    </>
  );
});

export default ImagesModal;
